import { uniq } from 'lodash';

import { type AllInstructionsData } from 'src/data/generation';
import { type GenerationTool, type SubGenerationToolProps } from 'src/data/generationTool';
import type { ProjectType } from 'src/data/project';
import { type Entity } from 'src/lib/collection';
import { parseJson } from 'src/lib/common';

import { type ImageOverlayProps } from '../image';

export interface MultiPromptTemplate extends Entity {
  name: string;
  description?: string;
  info?: string;
  fields?: unknown[];
  blocks?: unknown[];
  status?: string;
  templateGroup?: string;
  templateType?: string;
  templateOutputType?: string;
  projectType?: ProjectType;
  tags?: string[];
  smallImage?: string;
  bigImage?: string;
}

interface MultiPromptTemplateBlock extends Entity {
  type: GenerationTool;
}

export const TEMPLATE_OUTPUT_TYPE = {
  textOnly: 'text_only',
  textAndImage: 'text_and_image',
};

export type TemplateOutputType = (typeof TEMPLATE_OUTPUT_TYPE)[keyof typeof TEMPLATE_OUTPUT_TYPE];

export const doesTemplateOutputIncludeImages = (templateOutputType: TemplateOutputType): boolean =>
  templateOutputType === TEMPLATE_OUTPUT_TYPE.textAndImage;

export interface TemplateSubToolProps {
  generationTool: GenerationTool;
  blockId: string;
  stepName: string;
  isImageGenerationEnabled?: boolean;
  imageStyleId?: string;
  imageAspectRatio?: string;
  imageOverlayProperties?: ImageOverlayProps;
  tovInput?: string;
  structureInstructionsData?: AllInstructionsData;
  subGenerationToolsProps?: Record<GenerationTool, SubGenerationToolProps>;
}

export interface SubToolParams {
  blockId: string;
  newValue: AllInstructionsData;
  isMixture?: boolean;
  subToolKey?: GenerationTool;
}

export interface TemplatePromptInstructionItem {
  tool: GenerationTool;
  title: string;
  structureInstructionsData: AllInstructionsData;
}
export interface TemplateAdvancedSettingsDraft {
  promptInstructionItems: TemplatePromptInstructionItem[];
  subToolProps: TemplateSubToolProps[];
}

export const getMultiPromptTemplateBlocks = (
  template: MultiPromptTemplate
): MultiPromptTemplateBlock[] => {
  if (!template?.blocks) {
    return [];
  }

  if (Array.isArray(template.blocks)) {
    return template.blocks as MultiPromptTemplateBlock[];
  }

  return parseJson<MultiPromptTemplateBlock[]>(template.blocks) || [];
};

export const getMultiPromptTemplateBlockTypes = (
  template: MultiPromptTemplate
): GenerationTool[] => {
  if (!template?.blocks) {
    return [];
  }

  const blocks = getMultiPromptTemplateBlocks(template);
  const blockTypes = blocks.map((block) => block.type);
  return uniq(blockTypes);
};
